import React, { useState } from "react";
import { Briefcase, Building2, PartyPopper, ArrowRight, FileText, Send, Gavel, ShieldCheck, MessagesSquare, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  {
    v: "agent",
    icon: <Briefcase className="w-4 h-4" />,
    label: "Travel Agents",
    intro: "Source premium DMCs for your leisure, honeymoon, or MICE clients — apples-to-apples, in minutes.",
    steps: [
      { n: "01", title: "Post your RFP", d: "Destination, pax, dates, budget. MICE specifics if any (hall layout, AV, gala). Takes ~90 seconds.", icon: <FileText className="w-5 h-5" /> },
      { n: "02", title: "Verified DMCs bid live", d: "Top-10 matched DMCs receive it instantly. Bids stream into your dashboard in real time.", icon: <Send className="w-5 h-5" /> },
      { n: "03", title: "AI apples-to-apples check", d: "Every bid is scored on completeness, price outliers, substitutions. No hidden gaps.", icon: <Gavel className="w-5 h-5" /> },
      { n: "04", title: "Award · escrow secures it", d: "Pick a bid, we hold the deposit in escrow until service is delivered. Zero trust drama.", icon: <ShieldCheck className="w-5 h-5" /> },
    ],
    cta: { to: "/register", state: { role: "agent" }, label: "Join as Travel Agent" },
  },
  {
    v: "dmc",
    icon: <Building2 className="w-4 h-4" />,
    label: "DMC Partners",
    intro: "Get high-intent leads matched to your destination expertise. Bid to win. Get paid via escrow.",
    steps: [
      { n: "01", title: "Apply & get verified", d: "Register in 60 seconds. Trawell Ops reviews KYC + agreement in 24–48 hrs, then activates you.", icon: <ShieldCheck className="w-5 h-5" /> },
      { n: "02", title: "Receive matched RFPs", d: "Only briefs for destinations you actually serve — via email + dashboard + WhatsApp.", icon: <FileText className="w-5 h-5" /> },
      { n: "03", title: "Submit line-item bid", d: "Pricing table with substitutions, inclusions, exclusions. Full transparency, faster wins.", icon: <Send className="w-5 h-5" /> },
      { n: "04", title: "Win · deliver · get paid", d: "Escrow releases in milestones (20/40/30/10 for MICE). Chase-free payouts, every time.", icon: <Trophy className="w-5 h-5" /> },
    ],
    cta: { to: "/register", state: { role: "dmc" }, label: "Apply as DMC" },
  },
  {
    v: "mice_operator",
    icon: <PartyPopper className="w-4 h-4" />,
    label: "MICE Operators",
    intro: "Specialised group procurement — conferences, incentives, gala dinners. Compete on your event edge.",
    steps: [
      { n: "01", title: "Register your event studio", d: "Tell us your capacity bands, hall specs, F&B, AV depth. Ops verifies in 24–48 hrs.", icon: <Building2 className="w-5 h-5" /> },
      { n: "02", title: "See group RFPs first", d: "MICE-tagged RFPs (100–2,500 pax) are routed to certified operators before general pool.", icon: <FileText className="w-5 h-5" /> },
      { n: "03", title: "Bid with structured pricing", d: "Per-pax, per-hall, per-day line items. AI flags outliers so buyers compare like-for-like.", icon: <MessagesSquare className="w-5 h-5" /> },
      { n: "04", title: "Escrow · milestone releases", d: "20% at signing · 40% pre-event · 30% at delivery · 10% wrap. Predictable cash-flow.", icon: <ShieldCheck className="w-5 h-5" /> },
    ],
    cta: { to: "/register", state: { role: "mice_operator" }, label: "Apply as MICE Operator" },
  },
];

export default function HowItWorks() {
  const [active, setActive] = useState("agent");
  const tab = TABS.find((t) => t.v === active);
  return (
    <section id="how" className="border-y border-hairline bg-ledger py-24" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="lg:max-w-2xl">
            <p className="overline">How TrawellB2B works</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink mt-4">
              Four steps. <span className="text-brand">Zero drama.</span>
            </h2>
            <p className="text-muted mt-3 text-base">
              Whether you post the brief or bid on it — TrawellB2B compresses B2B travel sourcing from days to minutes.
              Pick your side to see the flow.
            </p>
          </div>
          <div className="hiw-tabs w-full lg:w-auto" role="tablist" data-testid="how-tabs">
            {TABS.map((t) => (
              <button
                key={t.v}
                role="tab"
                aria-selected={active === t.v}
                onClick={() => setActive(t.v)}
                className={`hiw-tab inline-flex items-center gap-2 ${active === t.v ? "hiw-tab-active" : ""}`}
                data-testid={`how-tab-${t.v}`}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="text-ink text-lg md:text-xl leading-relaxed max-w-3xl mb-10" data-testid={`how-intro-${active}`}>
          {tab.intro}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid={`how-steps-${active}`}>
          {tab.steps.map((s) => (
            <div key={s.n} className="step-card fade-up">
              <span className="step-num">STEP {s.n}</span>
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-ledger text-brand mt-3 mb-4 border border-hairline">
                {s.icon}
              </div>
              <h3 className="font-display text-ink text-xl font-bold">{s.title}</h3>
              <p className="text-sm text-muted mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to={tab.cta.to}
            state={tab.cta.state}
            className="btn-primary"
            data-testid={`how-cta-${active}`}
          >
            {tab.cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
          <span className="text-sm text-muted">
            Free to join · Ops-approved in 24–48 hrs · No credit card
          </span>
        </div>
      </div>
    </section>
  );
}
